/**
 * Générateur — Attendu 200 — O10 « Il écrit plusieurs scripts fonctionnant en
 * parallèle pour gérer des interactions et créer des jeux. »
 * Niveau : 4ème | Thème #12 Algorithmique (O10, Niveau 3) | Session B.
 * Source prof : O10 MUET en texte ; critère canonique = l'image page 2 (deux
 * lutins, scripts en parallèle, interaction « faire dire une phrase quand l'autre
 * le touche ») + « jeu mobilisant les capacités des niveaux 1-3 ». → BANQUE.
 *
 * ADAPTATIONS scoring : le parallélisme n'a pas d'exécution scorable → QCM
 * CONCRÉTISÉS (prénoms, noms de lutins, contexte de jeu tirés) contre U-VARIANTES.
 *
 * 4 cartes :
 *  1. parallele    — deux scripts « drapeau cliqué » : que se passe-t-il ? (QCM).
 *  2. message      — coordonner deux lutins par un message (QCM).
 *  3. interaction  — faire réagir un lutin quand il en touche un autre (QCM).
 *  4. jeu_capacites — quelles capacités un jeu mobilise-t-il ? (QCM multiple).
 */

import {
    randomChoice, shuffle, makeQcm,
} from '../../../utils';

const ATTENDU_ID = 200;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PARALLELE = 'Deux scripts en parallèle';
const L_MESSAGE = 'Coordonner deux lutins par un message';
const L_INTERACTION = 'Réagir quand un lutin en touche un autre';
const L_JEU = 'Un jeu : quelles capacités ?';

const PRENOMS = ['Léna', 'Youri', 'Sacha', 'Nina', 'Timéo', 'Awa', 'Malo', 'Jade'];
const LUTINS = ['le chat', 'la souris', 'le dragon', 'la fée', 'le robot', 'le fantôme', 'le poisson'];

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
// « de + article » : le X → du X ; la X → de la X ; l'X → de l'X
const deLutin = (l) => (l.startsWith('le ') ? `du ${l.slice(3)}` : `de ${l}`);

// ═════════ Carte 1 — deux scripts en parallèle (QCM) ═════════

const JEUX_CTX = ['labyrinthe', 'course', 'casse-briques', 'plateforme', 'attrape-fruits', 'ping-pong'];

const genParallele = () => {
    const prenom = randomChoice(PRENOMS);
    const jeu = randomChoice(JEUX_CTX);
    const correct = 'Les deux scripts démarrent en même temps et s\'exécutent en parallèle.';
    const choix = [
        correct,
        'Seul le premier script écrit démarre.',
        'Les scripts démarrent l\'un après l\'autre, jamais ensemble.',
        'Rien ne se passe : il ne peut y avoir qu\'un seul chapeau « drapeau cliqué ».',
    ];
    const texte = `<p><b>Dans son jeu de ${jeu}, ${prenom} a écrit deux scripts qui commencent tous `
        + 'les deux par « quand le drapeau vert est cliqué ». Que se passe-t-il quand on clique '
        + 'sur le drapeau ?</b></p>';
    const payload = makeQcm(choix, correct, texte, opts(L_PARALLELE));
    payload._meta.params = { prenom, jeu };
    return payload;
};

// ═════════ Carte 2 — coordonner par un message (QCM) ═════════

const genMessage = () => {
    const [a, b] = shuffle([...LUTINS]).slice(0, 2);
    const correct = `${cap(a)} envoie un message, et ${b} y réagit avec « quand je reçois ce message ».`;
    const choix = [
        correct,
        `${cap(a)} doit cliquer sur ${b} pour le prévenir.`,
        `${cap(b)} devine tout seul, sans aucun bloc.`,
        `Il faut fusionner ${a} et ${b} en un seul lutin.`,
    ];
    const texte = `<p><b>Dans un jeu, ${a} veut prévenir ${b} qu'il l'a attrapé. `
        + 'Comment faire communiquer les deux lutins ?</b></p>';
    const payload = makeQcm(choix, correct, texte, opts(L_MESSAGE));
    payload._meta.params = { a, b };
    return payload;
};

// ═════════ Carte 3 — réagir à un contact (QCM) ═════════

const genInteraction = () => {
    const [a, b] = shuffle([...LUTINS]).slice(0, 2);
    const phrase = randomChoice(['Touché !', 'Attrapé !', 'Perdu !', 'Aïe !']);
    const correct = `si « touche ${b} ? » alors dire « ${phrase} »`;
    const choix = [
        correct,
        `quand le drapeau vert est cliqué, dire « ${phrase} »`,
        `répéter 10 fois [ dire « ${phrase} » ]`,
        `mettre le score à « ${phrase} »`,
    ];
    const texte = `<p><b>On veut que ${a} dise « ${phrase} » dès qu'il touche ${b}. `
        + `Quel bloc utiliser dans le script ${deLutin(a)} ?</b></p>`;
    const payload = makeQcm(choix, correct, texte, opts(L_INTERACTION));
    payload._meta.params = { a, b, phrase };
    return payload;
};

// ═════════ Carte 4 — quelles capacités ? (QCM multiple) ═════════

const JEUX = ['un labyrinthe', 'un jeu de course', 'un casse-briques', 'un jeu de plateforme', 'un attrape-fruits'];
const INTRUS_JEU = [
    'imprimer le code du programme sur papier',
    'changer la résolution de l\'écran de l\'ordinateur',
    'connaître le poids réel du lutin',
    'traduire le jeu en anglais automatiquement',
];

const genJeuCapacites = () => {
    const jeu = randomChoice(JEUX);
    const capacites = [
        'des boucles (répéter …)',
        'des conditions (si … alors)',
        'des variables (par exemple le score)',
        'des événements (quand une touche est pressée)',
    ];
    const intrus = shuffle([...INTRUS_JEU]).slice(0, 2);
    const choix = shuffle([...capacites, ...intrus]);
    const texte = `<p><b>Pour programmer ${jeu} à deux lutins, quelles capacités des niveaux 1 à 3 `
        + 'faut-il mobiliser ? (Coche toutes les bonnes.)</b></p>';
    const payload = makeQcm(choix, capacites, texte, opts(L_JEU), true);
    payload._meta.params = { jeu };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    parallele_qcm_unique: genParallele,
    message_qcm_unique: genMessage,
    interaction_qcm_unique: genInteraction,
    jeu_capacites_qcm_multiple: genJeuCapacites,
};

const CARTES = [
    { id: 'parallele', label: L_PARALLELE },
    { id: 'message', label: L_MESSAGE },
    { id: 'interaction', label: L_INTERACTION },
    { id: 'jeu_capacites', label: L_JEU },
];

export const CATEGORIES = CARTES.map((c) => ({
    id: c.id,
    label: c.label,
    formatsList: Object.keys(GENERATORS).filter((k) => k.startsWith(`${c.id}_`))
        .map((k) => ({ key: k, label: c.label })),
}));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
